/**
 * Lane splits that Mapbox does not call a maneuver.
 *
 * Reported from the road: "some forks just assumed i was in a lane and never
 * notified about the fork. i was in the third lane, navi said nothing about
 * this big fork and had i stayed in that lane, i would have gone the wrong
 * way."
 *
 * The cause is not which junction we read - that was fixed in lanes.ts - but
 * WHICH JUNCTIONS WE LOOK AT AT ALL. Every lane feature in this app reads
 * `intersections[0]`, the junction a maneuver happens at. A step, though,
 * carries every intersection along its length, and the carriageway can divide
 * at one of them without Mapbox emitting any maneuver: from the router's point
 * of view the route simply continues on the same road, so there is nothing to
 * announce. From the driver's seat the road just split four ways.
 *
 * Measured over six Thai routes: 204 junctions where some lanes leave the route
 * while others stay, of which only a handful sit at a maneuver. The app was
 * blind to the rest.
 *
 * Of those 204, half are ordinary turn lanes - a left-turn pocket, clearly
 * signed, which the driver reads off the arrow painted on the road. They need
 * no warning. The other 105 are the dangerous kind:
 *
 *     (straight) (straight) (straight) [straight]
 *
 * Four lanes, every one painted with a straight arrow, and only the rightmost
 * stays on route. Nothing the driver can see through the windscreen
 * distinguishes lane three from lane four. That is the junction in the report,
 * and it is the only kind this module surfaces - a warning at every turn pocket
 * would be the noise the significance filter exists to prevent.
 */

import type { Lane, RouteStep } from '../core/types';
import { laneValid } from './lanes';
import { straightLaneContinuation } from './laneVisibility';

export interface Divergence {
  /** Distance along the whole route to the split, metres. */
  along: number;
  /**
   * Where the run of identical splits ends. A ramp approach repeats the same
   * lane model at every junction along it; that is one decision, not four.
   */
  until: number;
  lanes: Lane[];
  /** Step this junction belongs to, for context and for keying speech. */
  stepIndex: number;
  /** Stable identity, so a split is announced once however long the approach. */
  key: string;
}

/** Lanes signed the same way, one staying on route and one not. */
function isDeceptive(lanes: Lane[]): boolean {
  // Reverses the earlier straight-lane warning policy: the driver's highway
  // replay produced repeated panels while staying straight on 24 / AH121.
  // A split without a route maneuver does not justify that interruption.
  if (straightLaneContinuation(lanes)) return false;
  const valid = lanes.filter(laneValid);
  if (!valid.length || valid.length === lanes.length) return false;

  const signs = (l: Lane): string[] => l.indications ?? [];
  const invalid = lanes.filter((l) => !laneValid(l));

  // The test is per-ARROW, not just "straight": two lanes both signed
  // `slight right` that lead to different roads deceive exactly as badly.
  for (const good of valid) {
    for (const bad of invalid) {
      if (signs(good).some((s) => signs(bad).includes(s))) return true;
    }
  }
  return false;
}

/** Signature of a lane model, for collapsing a repeated run into one event. */
function signature(lanes: Lane[]): string {
  return lanes.map((l) => `${laneValid(l) ? 1 : 0}:${(l.indications ?? []).join('/')}`).join('|');
}

/**
 * Which side of the carriageway the surviving lanes sit on.
 *
 * Runs are collapsed on THIS rather than on the exact lane model. A split
 * spread over a few hundred metres is described slightly differently at each
 * junction along it - four lanes here, three there, as a lane is added or drops
 * away - so signature matching leaves a cluster of near-identical warnings
 * metres apart. Measured before this: two warnings at 2.5 km and two more at
 * 4.3 km on one Bangkok route. What the driver has to do is the same at all of
 * them - move right, or move left - and that is what should be said once.
 */
function sideOf(lanes: Lane[]): 'left' | 'right' | 'centre' {
  const idx = lanes.map((l, i) => (laneValid(l) ? i : -1)).filter((i) => i >= 0);
  if (idx[0] === 0) return 'left';
  if (idx[idx.length - 1] === lanes.length - 1) return 'right';
  return 'centre';
}

/**
 * Distance along the route at which the same lane model must be treated as a
 * fresh decision rather than a continuation of the last one.
 */
const RUN_GAP_M = 800;

/**
 * Every deceptive split on the route, in order.
 *
 * `cumulative[i]` is the distance along the route to geometry vertex i, and
 * `intersection.geometry_index` indexes that same geometry - verified against
 * the API at 665 of 665 intersections with zero error, so the position is
 * exact rather than interpolated.
 */
export function buildDivergences(
  steps: { step: RouteStep; startDistance: number }[],
  cumulative: number[],
  /** `significant[i]` is the maneuver at the start of step i. */
  significant: readonly boolean[],
): Divergence[] {
  const raw: Divergence[] = [];

  steps.forEach(({ step, startDistance }, stepIndex) => {
    const ints = step.intersections ?? [];
    ints.forEach((x, j) => {
      // Index 0 is the maneuver's own junction, already covered by the lane bar
      // and the fork strip. This module exists for the ones after it.
      if (j === 0) return;
      const lanes = x.lanes;
      if (!lanes?.length) return;
      if (!isDeceptive(lanes)) return;

      const gi = x.geometry_index;
      const along =
        gi != null && cumulative[gi] != null ? cumulative[gi] : startDistance;

      raw.push({
        along,
        until: along,
        lanes,
        stepIndex,
        key: `${stepIndex}:${signature(lanes)}`,
      });
    });
  });

  raw.sort((a, b) => a.along - b.along);

  // Collapse runs: the same lane model repeating every 100 m down a ramp
  // approach is one decision. Keep the EARLIEST position, because that is when
  // the driver still has room to move, and carry `until` to the last of the run
  // so the warning stays up until the split is actually behind them.
  const out: Divergence[] = [];
  for (const d of raw) {
    const prev = out[out.length - 1];
    // A turn, U-turn, ramp or other announced maneuver ends the current lane
    // decision. Never let a repeated-run merge carry a post-maneuver lane
    // model backwards across it: that is how lanes after a U-turn appeared
    // while the driver was still approaching the U-turn.
    let crossesSignificantManeuver = false;
    if (prev) {
      for (let i = prev.stepIndex + 1; i <= d.stepIndex; i++) {
        if (significant[i]) {
          crossesSignificantManeuver = true;
          break;
        }
      }
    }
    if (
      prev &&
      !crossesSignificantManeuver &&
      sideOf(prev.lanes) === sideOf(d.lanes) &&
      d.along - prev.until <= RUN_GAP_M
    ) {
      prev.until = d.along;
      // Keep the model with the MOST lanes: it is the widest description of the
      // split, and a driver counting lanes out of the window needs the count
      // that matches the road at its widest point rather than after one has
      // already peeled away.
      if (d.lanes.length > prev.lanes.length) prev.lanes = d.lanes;
      continue;
    }
    out.push(d);
  }
  return out;
}

/**
 * How far ahead a split is worth showing. Same reasoning as the fork strip:
 * wide enough to cross lanes at speed, floored so it is useful in town.
 */
export function divergenceRange(speedMs: number): number {
  return Math.min(2500, Math.max(600, speedMs * 45));
}

/** The next split the driver has not yet passed, or null. */
export function nextDivergence(
  list: Divergence[],
  along: number,
  speedMs: number,
): Divergence | null {
  const range = divergenceRange(speedMs);
  for (const d of list) {
    if (d.until < along) continue; // behind us
    if (d.along - along > range) return null; // not near enough yet
    return d;
  }
  return null;
}
